import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ImageUploadField } from "@/components/ImageUploadField";
import { Home, Save, RotateCcw, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Immutable deep setter — setPath(obj, "hero.online.label", "x")
function setPath(obj, path, value) {
  const keys = Array.isArray(path) ? path : path.split(".");
  const clone = structuredClone(obj);
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    if (cur[keys[i]] === undefined || cur[keys[i]] === null) cur[keys[i]] = {};
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

const ICON_OPTIONS = ["Trophy", "Clock", "ShieldCheck", "MapPin", "MonitorPlay"];

// Editor context supplies cfg + set so the field components below can live at MODULE scope.
// (Defining them inside the page component gave them a new identity every render, which
// remounted inputs on each keystroke → mobile keyboard dropped after every character.)
const EditorCtx = createContext({ cfg: {}, set: () => {} });
const useEditor = () => useContext(EditorCtx);

function F({ label, path, placeholder, mono }) {
  const { cfg, set } = useEditor();
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={getAt(cfg, path) ?? ""} onChange={(e) => set(path, e.target.value)} placeholder={placeholder}
        data-testid={`home-${path.replaceAll(".", "-")}`}
        className={`bg-black border-[#27272A] text-white rounded-sm ${mono ? "font-mono text-xs" : ""}`} />
    </div>
  );
}
function A({ label, path, rows = 3 }) {
  const { cfg, set } = useEditor();
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Textarea value={getAt(cfg, path) ?? ""} onChange={(e) => set(path, e.target.value)} rows={rows}
        data-testid={`home-${path.replaceAll(".", "-")}`}
        className="bg-black border-[#27272A] text-white rounded-sm text-sm" />
    </div>
  );
}
function N({ label, path }) {
  const { cfg, set } = useEditor();
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input type="number" value={getAt(cfg, path) ?? 0} onChange={(e) => set(path, Number(e.target.value))}
        data-testid={`home-${path.replaceAll(".", "-")}`}
        className="bg-black border-[#27272A] text-white rounded-sm font-mono w-32" />
    </div>
  );
}
function Img({ label, path }) {
  const { cfg, set } = useEditor();
  return <ImageUploadField label={label} value={getAt(cfg, path) || ""} onChange={(url) => set(path, url)} testid={`home-img-${path.replaceAll(".", "-")}`} />;
}
function Toggle({ path, label = "Show this section" }) {
  const { cfg, set } = useEditor();
  return (
    <div className="flex items-center justify-between border border-[#27272A] bg-black px-4 py-3 rounded-sm">
      <span className="text-sm text-zinc-300">{label}</span>
      <Switch checked={getAt(cfg, path) !== false} onCheckedChange={(v) => set(path, v)} data-testid={`home-toggle-${path.replaceAll(".", "-")}`} />
    </div>
  );
}
function StringList({ path, itemLabel = "Item" }) {
  const { cfg, set } = useEditor();
  const arr = getAt(cfg, path) || [];
  return (
    <div className="space-y-2">
      {arr.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => set(path, arr.map((x, j) => (j === i ? e.target.value : x)))}
            className="bg-black border-[#27272A] text-white rounded-sm text-sm" data-testid={`home-${path.replaceAll(".", "-")}-${i}`} />
          <Button type="button" variant="outline" onClick={() => set(path, arr.filter((_, j) => j !== i))}
            className="border-[#27272A] bg-black text-zinc-400 hover:text-red-400 rounded-sm px-2"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set(path, [...arr, ""])}
        className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> Add {itemLabel}</Button>
    </div>
  );
}
function ObjList({ path, fields, blank, addLabel }) {
  const { cfg, set } = useEditor();
  const arr = getAt(cfg, path) || [];
  const upd = (i, key, val) => set(path, arr.map((x, j) => (j === i ? { ...x, [key]: val } : x)));
  return (
    <div className="space-y-3">
      {arr.map((item, i) => (
        <div key={i} className="border border-[#27272A] bg-black p-3 rounded-sm space-y-2 relative">
          <button type="button" onClick={() => set(path, arr.filter((_, j) => j !== i))}
            className="absolute top-2 right-2 text-zinc-500 hover:text-red-400" data-testid={`home-${path.replaceAll(".", "-")}-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          {fields.map((f) => (
            f.type === "textarea" ? (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                <Textarea value={item[f.key] ?? ""} onChange={(e) => upd(i, f.key, e.target.value)} rows={2} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm text-sm" />
              </div>
            ) : f.type === "bool" ? (
              <div key={f.key} className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">{f.label}</span>
                <Switch checked={!!item[f.key]} onCheckedChange={(v) => upd(i, f.key, v)} />
              </div>
            ) : f.type === "icon" ? (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                <select value={item[f.key] ?? ICON_OPTIONS[0]} onChange={(e) => upd(i, f.key, e.target.value)}
                  className="w-full h-9 bg-[#0F0F0F] border border-[#27272A] text-white rounded-sm px-2 text-sm">
                  {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ) : f.type === "image" ? (
              <ImageUploadField key={f.key} label={f.label} value={item[f.key] || ""} onChange={(url) => upd(i, f.key, url)} testid={`home-${path.replaceAll(".", "-")}-img-${i}`} />
            ) : (
              <div key={f.key} className="space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
                <Input value={item[f.key] ?? ""} onChange={(e) => upd(i, f.key, e.target.value)} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm text-sm" />
              </div>
            )
          ))}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set(path, [...arr, { ...blank }])}
        className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs"><Plus className="h-3 w-3 mr-1" /> {addLabel}</Button>
    </div>
  );
}

export default function AdminHomePage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/home-config").then(({ data }) => setCfg(data)).catch(() => toast.error("Failed to load home content"));
  useEffect(() => { load(); }, []);

  const set = (path, value) => setCfg((c) => setPath(c, path, value));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/home-config", { config: cfg });
      setCfg(data);
      toast.success("Home page saved. Publish/redeploy to push it live.");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the ENTIRE home page to the original default content? This can't be undone.")) return;
    try {
      const { data } = await api.post("/admin/home-config/reset");
      setCfg(data);
      toast.success("Reverted to defaults.");
    } catch { toast.error("Reset failed"); }
  };

  if (!cfg) return <div className="p-10 text-zinc-500">Loading…</div>;

  const sectionClass = "border border-[#27272A] bg-[#0F0F0F] rounded-sm";

  return (
    <EditorCtx.Provider value={{ cfg, set }}>
    <div className="p-6 md:p-10 space-y-6 max-w-4xl" data-testid="admin-home-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Content</p>
          <h1 className="font-display text-3xl text-white uppercase tracking-tight flex items-center gap-2"><Home className="h-6 w-6 text-[#FF331F]" /> Home Page</h1>
          <p className="text-xs text-zinc-500 mt-1">Edit every section of the public home page. Changes go live after you Publish/redeploy.</p>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white border border-[#27272A] px-3 py-2 rounded-sm"><ExternalLink className="h-3.5 w-3.5" /> Preview</a>
          <Button onClick={reset} variant="outline" className="border-[#27272A] bg-black text-zinc-300 hover:text-white rounded-sm text-xs uppercase tracking-widest" data-testid="home-reset"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold" data-testid="home-save"><Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}</Button>
        </div>
      </header>

      <Accordion type="multiple" defaultValue={["seo", "hero"]} className="space-y-3">
        {/* SEO / PAGE TITLE */}
        <AccordionItem value="seo" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">SEO — Page Title & Description</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <F label="Browser / Google title tag" path="seo.title" />
            <A label="Meta description (shown under the title in Google)" path="seo.description" rows={3} />
            <p className="text-[11px] text-zinc-600">This is the title that shows in the browser tab and in Google search results for the homepage. Keep it under ~60 characters; description under ~160.</p>
          </AccordionContent>
        </AccordionItem>

        {/* HERO */}
        <AccordionItem value="hero" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Hero</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="hero.enabled" />
            <F label="Kicker (small red text)" path="hero.kicker" />
            <div className="space-y-2">
              <A label="Headline — before accent" path="hero.title_pre" rows={2} />
              <A label="Headline — accent (red)" path="hero.title_accent" rows={2} />
              <A label="Headline — after accent" path="hero.title_post" rows={2} />
              <p className="text-[11px] text-zinc-600">Tip: press Enter inside any headline field to add a line break — it will show as a new line on the page. (Usually you'll put the break at the end of "before accent", e.g. type "Online boxing lessons," then Enter then "live with ".)</p>
            </div>
            <A label="Subtitle" path="hero.subtitle" />
            <Img label="Background image" path="hero.bg_image" />
            <div className="grid md:grid-cols-2 gap-3">
              <div className="border border-[#27272A] p-3 rounded-sm space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">Online card</div>
                <F label="Label" path="hero.online.label" />
                <F label="Sub-label" path="hero.online.sublabel" />
                <F label="Link (#services, /services, or URL)" path="hero.online.href" mono />
              </div>
              <div className="border border-[#27272A] p-3 rounded-sm space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">In-Person card</div>
                <F label="Label" path="hero.inperson.label" />
                <F label="Sub-label" path="hero.inperson.sublabel" />
                <F label="Link (#in-person, /in-person-training, or URL)" path="hero.inperson.href" mono />
              </div>
            </div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Trust badges</Label>
            <ObjList path="hero.badges" addLabel="Add badge" blank={{ icon: "Trophy", text: "" }}
              fields={[{ key: "icon", label: "Icon", type: "icon" }, { key: "text", label: "Text" }]} />
          </AccordionContent>
        </AccordionItem>

        {/* SERVICES */}
        <AccordionItem value="services" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Services / Programs</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="services.enabled" />
            <F label="Kicker" path="services.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="services.heading_line1" />
              <F label="Heading line 2" path="services.heading_line2" />
            </div>
            <A label="Intro paragraph" path="services.intro" />
            <div className="border border-[#27272A] p-3 rounded-sm space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">Featured program</div>
              <div className="grid md:grid-cols-2 gap-2">
                <F label="Tag" path="services.featured.tag" />
                <F label="Title" path="services.featured.title" />
                <F label="Duration" path="services.featured.duration" />
                <F label="Location text" path="services.featured.location" />
                <N label="Price ($)" path="services.featured.price" />
                <F label="Price note" path="services.featured.price_note" />
              </div>
              <A label="Description" path="services.featured.desc" />
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Perks</Label>
              <ObjList path="services.featured.perks" addLabel="Add perk" blank={{ text: "", highlight: false }}
                fields={[{ key: "text", label: "Perk" }, { key: "highlight", label: "Highlight (red box)", type: "bool" }]} />
              <div className="grid grid-cols-2 gap-2">
                <F label="Button label" path="services.featured.cta_label" />
                <F label="Button link" path="services.featured.cta_href" mono />
              </div>
            </div>
            <div className="border border-[#27272A] p-3 rounded-sm space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">Single session card</div>
              <div className="grid md:grid-cols-2 gap-2">
                <F label="Tag" path="services.single.tag" />
                <F label="Title" path="services.single.title" />
                <F label="Duration" path="services.single.duration" />
                <N label="Price ($)" path="services.single.price" />
                <N label="Alumni price ($)" path="services.single.alumni_price" />
                <F label="Button link" path="services.single.cta_href" mono />
              </div>
              <A label="Description" path="services.single.desc" />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* ABOUT */}
        <AccordionItem value="about" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">About the Coach</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="about.enabled" />
            <F label="Kicker" path="about.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="about.heading_pre" />
              <F label="Heading line 2 — lead text" path="about.heading_lead" />
              <F label="Heading accent (red)" path="about.heading_accent" />
              <F label="Heading — after accent" path="about.heading_post" />
            </div>
            <A label="Body paragraph 1" path="about.body1" />
            <A label="Body paragraph 2" path="about.body2" />
            <Img label="Coach photo" path="about.image" />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Stats (3)</Label>
            <ObjList path="about.stats" addLabel="Add stat" blank={{ value: "", label: "", accent: false }}
              fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }, { key: "accent", label: "Red accent", type: "bool" }]} />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Specialties</Label>
            <StringList path="about.specialties" itemLabel="specialty" />
            <F label="Location line" path="about.location_line" />
          </AccordionContent>
        </AccordionItem>

        {/* METHOD */}
        <AccordionItem value="method" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">The Method (statement banner)</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="method.enabled" />
            <F label="Kicker" path="method.kicker" />
            <F label="Heading" path="method.heading" />
            <A label="Body paragraph 1" path="method.body1" />
            <A label="Body paragraph 2" path="method.body2" />
            <Img label="Artwork image" path="method.art_image" />
          </AccordionContent>
        </AccordionItem>

        {/* IN ACTION */}
        <AccordionItem value="in_action" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">In Action (photo grid)</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="in_action.enabled" />
            <F label="Kicker" path="in_action.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="in_action.heading_line1" />
              <F label="Heading line 2" path="in_action.heading_line2" />
            </div>
            <F label="Subtitle" path="in_action.subtitle" />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Photos (first is the large tile)</Label>
            <ObjList path="in_action.photos" addLabel="Add photo" blank={{ image: "", caption: "", kicker: "" }}
              fields={[{ key: "image", label: "Photo", type: "image" }, { key: "caption", label: "Caption" }, { key: "kicker", label: "Kicker (large tile only)" }]} />
            <F label="Instagram handle (no @)" path="in_action.instagram_handle" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Button label" path="in_action.cta_label" />
              <F label="Button link" path="in_action.cta_href" mono />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* TESTIMONIALS */}
        <AccordionItem value="testimonials" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Testimonials</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="testimonials.enabled" />
            <F label="Kicker" path="testimonials.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="testimonials.heading_line1" />
              <F label="Heading line 2" path="testimonials.heading_line2" />
            </div>
            <div className="border border-[#27272A] p-3 rounded-sm space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">Featured spotlight</div>
              <A label="Quote" path="testimonials.featured.quote" />
              <div className="grid grid-cols-2 gap-2">
                <F label="Author" path="testimonials.featured.author" />
                <F label="Badge" path="testimonials.featured.badge" />
              </div>
            </div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Supporting reviews</Label>
            <ObjList path="testimonials.items" addLabel="Add review" blank={{ quote: "", author: "" }}
              fields={[{ key: "quote", label: "Quote", type: "textarea" }, { key: "author", label: "Author" }]} />
          </AccordionContent>
        </AccordionItem>

        {/* APPAREL */}
        <AccordionItem value="apparel" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Apparel teaser</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="apparel.enabled" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Kicker" path="apparel.kicker" />
              <F label="Badge" path="apparel.badge" />
            </div>
            <F label="Heading" path="apparel.heading" />
            <A label="Body" path="apparel.body" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Button label" path="apparel.cta_label" />
              <F label="Shop link (URL)" path="apparel.cta_href" mono />
            </div>
            <Img label="Product image" path="apparel.image" />
          </AccordionContent>
        </AccordionItem>

        {/* IN-PERSON */}
        <AccordionItem value="in_person" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">In-Person (NJ request form)</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="in_person.enabled" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Kicker" path="in_person.kicker" />
              <F label="Badge" path="in_person.badge" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <F label="Heading line 1" path="in_person.heading_line1" />
              <F label="Heading line 2" path="in_person.heading_line2" />
              <F label="Heading accent (red)" path="in_person.heading_accent" />
            </div>
            <A label="Body" path="in_person.body" />
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Bullet points</Label>
            <StringList path="in_person.bullets" itemLabel="bullet" />
            <p className="text-[11px] text-zinc-600">The request form fields themselves stay fixed — only the copy above is editable.</p>
          </AccordionContent>
        </AccordionItem>

        {/* CLOSER */}
        <AccordionItem value="closer" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Final closer</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="closer.enabled" />
            <F label="Kicker" path="closer.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="closer.heading_line1" />
              <F label="Heading line 2" path="closer.heading_line2" />
            </div>
            <A label="Body" path="closer.body" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Primary button label" path="closer.primary_label" />
              <F label="Primary button link" path="closer.primary_href" mono />
              <F label="Secondary button label" path="closer.secondary_label" />
              <F label="Secondary button link" path="closer.secondary_href" mono />
            </div>
            <Img label="Coach photo" path="closer.image" />
          </AccordionContent>
        </AccordionItem>

        {/* CONTACT */}
        <AccordionItem value="contact" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Contact</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="contact.enabled" />
            <F label="Kicker" path="contact.kicker" />
            <div className="grid grid-cols-2 gap-2">
              <F label="Heading line 1" path="contact.heading_line1" />
              <F label="Heading line 2" path="contact.heading_line2" />
            </div>
            <A label="Body" path="contact.body" />
            <F label="Instagram handle (no @)" path="contact.instagram_handle" />
          </AccordionContent>
        </AccordionItem>

        {/* GOOGLE */}
        <AccordionItem value="google" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Google Maps & Reviews</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="google.enabled" />
            <F label="Kicker" path="google.kicker" />
            <F label="Heading" path="google.heading" />
            <A label="Subtext" path="google.subtext" />
            <F label="Google Maps listing link" path="google.maps_url" mono />
            <F label="Leave-a-review link" path="google.review_url" mono />
            <F label="Map embed URL (…&output=embed)" path="google.map_embed_url" mono />
            <div className="grid grid-cols-2 gap-2">
              <F label="Address / area (for SEO)" path="google.address" />
              <F label="Phone (optional, for SEO)" path="google.phone" />
            </div>
            <p className="text-[11px] text-zinc-600">Powers the map + "Leave a Review" block near Contact, and the LocalBusiness structured data for local SEO.</p>
          </AccordionContent>
        </AccordionItem>

        {/* EXTRAS */}
        <AccordionItem value="extras" className={sectionClass}>
          <AccordionTrigger className="px-4 text-white uppercase tracking-widest text-sm">Instagram feed & App section</AccordionTrigger>
          <AccordionContent className="px-4 pb-4 space-y-3">
            <Toggle path="instagram_feed.enabled" label="Show Instagram feed" />
            <F label="Instagram section title" path="instagram_feed.title" />
            <Toggle path="get_the_app.enabled" label="Show 'Get the app' section" />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end gap-2 sticky bottom-4">
        <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold shadow-lg" data-testid="home-save-bottom"><Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save home page"}</Button>
      </div>
    </div>
    </EditorCtx.Provider>
  );
}

// helper: read a dotted path
function getAt(obj, path) {
  return (Array.isArray(path) ? path : path.split(".")).reduce((o, k) => (o == null ? o : o[k]), obj);
}
